import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupData, Service, ValidationFunction } from 'webapp-types';

import CommonMetricAttributesUnitFieldsetComponent from 'webapp/components/common/metric-attributes-unit-fieldset';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class CommonPopupsMetricSelectionComponent extends PopupInnerModalComponent<PopupData.Common.MetricSelection> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	validateFieldset: null | ValidationFunction = null;

	@tracked metricSetupData: DSSetup.MetricSetupData;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.metricSetupData = this.popupData.metricSetupData || {
			key: '',
			spaceAccessId: null,
		};

		void this.mmMetricsService.prepareForMetricEditing();
	}

	get requireSingleOutput() {
		return !!this.popupData.requireSingleOutput;
	}

	@action
	bindFieldsetValidation(validationFn: ValidationFunction) {
		this.validateFieldset = validationFn;
	}

	@action
	changeMetricSetupData(newValue: DSSetup.MetricSetupData): void {
		this.metricSetupData = newValue;
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		const errors = this.validateFieldset ? Object.keys(this.validateFieldset()) : {};
		if (Object.keys(errors).length !== 0) {
			return;
		}

		this.submitInProgress = true;
		const saveResponse = this.popupData.onSave(this.metricSetupData);
		if (saveResponse?.then) {
			void saveResponse
				.then(() => {
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch();
		} else {
			this.close();
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Metrics" @size="lg" />
			Select metric
		</div>

		<div class="content">
			<CommonMetricAttributesUnitFieldsetComponent
				@model={{this.metricSetupData}}
				@changeMetricSetupData={{this.changeMetricSetupData}}
				@validationBinder={{this.bindFieldsetValidation}}
				@requireSingleOutput={{this.requireSingleOutput}}
				@onContentChange={{this.scheduleReposition}}
				@metricSelectOptions={{this.popupData.metricSelectOptions}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
